import { ReactElement, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import TopFirstView from './component/TopFirstView';
import Footer from '@/ui/Footer';
import { Button } from '@/components/Button';
import searchCalendarIcon from '@/assets/images/common/search-calendar-icon.png';
import { Accordion } from '@/components/Accordion';
import googleCalendarIcon from '@/assets/images/settings/google_calendar_icon.png';
import outlookCalendarIcon from '@/assets/images/common/microsoft-icon.png';
import { saveTopPageReferrer } from '@/utils/loginInfo';
import { useTranslation } from 'react-i18next';
import TopBenefits from './component/TopBenefits';
import TopUiGallery from './component/TopUiGallery';
import TopGuide from './component/TopGuide';
import TopUsecases from './component/TopUsecases';

type FaqTranslation = {
    title: string;
    content: string;
};

/**
 * トップページ
 * @returns トップページ
 */
function Top(): ReactElement {
    const { t, i18n } = useTranslation('top');

    const faqItems = useMemo(() => {
        const faqTranslations = t('faq.items', {
            returnObjects: true,
        }) as FaqTranslation[];
        return faqTranslations.map((item) => ({
            title: item.title,
            content: item.content,
        }));
    }, [t]);

    const metaTitle = t('meta.title');
    const metaDescription = t('meta.description');
    const metaKeywords = t('meta.keywords');
    const sectionTitles = {
        calendars: t('sections.calendars'),
        faq: t('sections.faq'),
    };
    const calendarLabels = {
        google: t('calendars.google'),
        outlook: t('calendars.outlook'),
    };
    const ctaHeadingLine1 = t('cta.headingLine1');
    const ctaHeadingLine2 = t('cta.headingLine2');
    const ctaButtonLabel = t('cta.buttonLabel');
    const currentLang = (i18n.language || 'en').split('-')[0];
    const htmlLang = currentLang === 'ja' ? 'ja' : 'en';
    const baseUrl = 'https://kotomil.com';
    const enHomeUrl = `${baseUrl}/?lang=en`;
    const canonicalUrl = currentLang === 'en' ? enHomeUrl : `${baseUrl}/`;
    const ogLocale = htmlLang === 'ja' ? 'ja_JP' : 'en_US';
    const ogAlternateLocale = htmlLang === 'ja' ? 'en_US' : 'ja_JP';
    const brandName = 'Kotomil';
    const alternateLinks = [
        { lang: 'ja', href: `${baseUrl}/` },
        { lang: 'en', href: enHomeUrl },
        { lang: 'x-default', href: enHomeUrl },
    ];

    // メイン処理

    useEffect(() => {
        // トップページのリファラー情報を保存
        saveTopPageReferrer();
    }, []);

    /**
     * レンダリング
     * @returns レンダリング
     */
    return (
        <>
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                {/* タイトル設定 */}
                <title>{metaTitle}</title>

                {/* 基本的なmeta情報 */}
                <meta name="description" content={metaDescription} />
                <meta name="author" content="Kotomil" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={canonicalUrl} />
                {alternateLinks.map(({ lang, href }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={href} />
                ))}

                {/* Open Graph設定 */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content={brandName} />
                <meta
                    property="og:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:locale" content={ogLocale} />
                <meta property="og:locale:alternate" content={ogAlternateLocale} />

                {/* Twitter Card設定 */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@KotomilApp" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta
                    name="twitter:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />

                {/* キーワード設定 */}
                <meta name="keywords" content={metaKeywords} />

                {/* 構造化データ（JSON-LD） */}
                <script type="application/ld+json">
                    {JSON.stringify([
                        {
                            '@context': 'https://schema.org',
                            '@type': 'WebSite',
                            name: brandName,
                            ...(htmlLang === 'ja' ? { alternateName: 'コトミル' } : {}),
                            url: canonicalUrl,
                            description: metaDescription,
                            inLanguage: htmlLang,
                            publisher: {
                                '@type': 'Organization',
                                name: brandName,
                            },
                            potentialAction: {
                                '@type': 'SearchAction',
                                target: 'https://kotomil.com/search?q={search_term_string}',
                                'query-input': 'required name=search_term_string',
                            },
                        },
                        {
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: brandName,
                            ...(htmlLang === 'ja' ? { alternateName: 'コトミル' } : {}),
                            url: canonicalUrl,
                            logo: 'https://kotomil.com/images/icon/kotomil_icon_512x512.png',
                            image: 'https://kotomil.com/images/ogp/kotomil_og_image.jpg',
                            description: metaDescription,
                            inLanguage: htmlLang,
                            sameAs: ['https://twitter.com/KotomilApp'],
                            contactPoint: {
                                '@type': 'ContactPoint',
                                contactType: 'customer service',
                                url: 'https://kotomil.com/contact',
                            },
                        },
                        {
                            '@context': 'https://schema.org',
                            '@type': 'WebApplication',
                            name: brandName,
                            url: canonicalUrl,
                            installUrl: 'https://kotomil.com/login',
                            description: metaDescription,
                            inLanguage: htmlLang,
                            applicationCategory: 'UtilitiesApplication',
                            browserRequirements:
                                'Requires JavaScript. Compatible with latest versions of Chrome, Firefox, Edge, Safari.',
                            operatingSystem: 'Web Browser',
                            offers: {
                                '@type': 'Offer',
                                price: '0',
                                priceCurrency: 'JPY',
                                availability: 'https://schema.org/InStock',
                            },
                        },
                    ])}
                </script>
            </Helmet>
            <main className="min-h-screen max-w-[1440px] mx-auto overflow-x-hidden">
                {/* ① Hero（価値 × UI × CTA） */}
                <TopFirstView />

                {/* ② ベネフィット（箇条書き + UI断片） */}
                <TopBenefits />

                {/* ③ 実際のUIギャラリー */}
                <TopUiGallery />

                {/* ④ 活用シーン */}
                <TopUsecases />

                {/* ⑥ 対応カレンダー */}
                <div className="relative bg-gray-50 py-20 lg:py-28">
                    {/* Decorative Wave Divider */}
                    <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
                        <svg
                            className="relative block w-full h-12 lg:h-20"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1200 120"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                                className="fill-white"
                            ></path>
                        </svg>
                    </div>

                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-16 text-gray-900">
                            {sectionTitles.calendars}
                        </h2>
                        <div className="flex flex-col md:flex-row justify-center items-center gap-16 md:gap-24">
                            {/* Google Calendar */}
                            <div className="flex flex-col items-center group">
                                <div className="w-28 h-28 lg:w-36 lg:h-36 bg-white rounded-3xl shadow-xl flex items-center justify-center p-6 mb-6 group-hover:shadow-2xl group-hover:scale-105 transition-all duration-300 border-2 border-gray-100">
                                    <img
                                        src={googleCalendarIcon}
                                        alt="Google Calendar"
                                        className="w-full h-full object-contain image-protection"
                                    />
                                </div>
                                <p className="text-xl lg:text-2xl font-bold text-gray-700 tracking-wider group-hover:text-gray-900 transition-colors">
                                    {calendarLabels.google}
                                </p>
                            </div>

                            {/* Separator */}
                            <div className="hidden md:block w-px h-32 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>

                            {/* Outlook Calendar */}
                            <div className="flex flex-col items-center group">
                                <div className="w-28 h-28 lg:w-36 lg:h-36 bg-white rounded-3xl shadow-xl flex items-center justify-center p-6 mb-6 group-hover:shadow-2xl group-hover:scale-105 transition-all duration-300 border-2 border-gray-100">
                                    <img
                                        src={outlookCalendarIcon}
                                        alt="Outlook Calendar"
                                        className="w-full h-full object-contain image-protection"
                                    />
                                </div>
                                <p className="text-xl lg:text-2xl font-bold text-gray-700 tracking-wider group-hover:text-gray-900 transition-colors">
                                    {calendarLabels.outlook}
                                </p>
                            </div>
                        </div>
                        <p className="mt-12 text-base lg:text-lg text-gray-500">
                            {t('calendars.note')}
                        </p>
                    </div>
                </div>

                {/* ⑦ ベーシックプラン・比較 (削除) */}

                {/* ⑧ FAQ */}
                <div className="relative py-20 lg:py-32 bg-white">
                    {/* Decorative Wave Divider */}
                    <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
                        <svg
                            className="relative block w-full h-12 lg:h-20"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1200 120"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                                className="fill-gray-50"
                            ></path>
                        </svg>
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute top-10 left-10 w-72 h-72 bg-blue-50/40 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-50/30 rounded-full blur-3xl"></div>

                    <div className="relative z-10">
                        <h2 className="text-center text-3xl lg:text-5xl font-black tracking-tight mb-20 text-gray-900">
                            {sectionTitles.faq}
                        </h2>
                        <div className="w-full max-w-3xl mx-auto px-4">
                            <Accordion
                                items={faqItems}
                                className="space-y-5"
                                titleClassName="text-lg lg:text-xl font-bold p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all duration-300 rounded-2xl flex justify-between items-center w-full text-left shadow-sm border border-gray-100"
                                contentClassName="bg-white px-6 lg:px-8 pb-6 lg:pb-8 pt-2 text-gray-600 leading-relaxed text-base lg:text-lg"
                            />
                        </div>
                    </div>
                </div>

                {/* ⑨ 3ステップの使い方 */}
                <TopGuide />

                {/* ⑩ CTA - Trust & Conversion Redesign */}
                <div className="relative py-24 lg:py-32 overflow-hidden">
                    {/* Background Image */}
                    <div className="absolute inset-0 bg-[url('@/assets/images/top/cta-background-image.png')] bg-cover bg-center"></div>

                    {/* Sophisticated Overlay */}
                    <div className="absolute inset-0 bg-kotomilThemaColor/50 backdrop-blur-[2px]"></div>

                    <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
                        {/* Heading with generous spacing */}
                        <h2 className="text-3xl lg:text-5xl font-black tracking-wider mb-12 text-gray-900">
                            <span>{ctaHeadingLine1}</span>
                            <br className="hidden md:block" />
                            <span className="md:ml-4 leading-[1.6]">{ctaHeadingLine2}</span>
                        </h2>

                        {/* Button Container */}
                        <div className="flex flex-col items-center justify-center w-full">
                            <Button
                                variant="default"
                                className="w-full max-w-md h-20 px-8 bg-white border-4 border-gray-900 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] rounded-xl mx-auto
                                        transition-all duration-200 ease-in-out
                                        hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-kotomilThemaColor
                                        active:translate-x-[6px] active:translate-y-[6px] active:shadow-none"
                                onClick={() => {
                                    window.location.href = '/login';
                                }}
                            >
                                <span className="inline-flex items-center justify-center gap-4 w-full">
                                    <span className="text-2xl font-bold tracking-widest">
                                        {ctaButtonLabel}
                                    </span>
                                    <img src={searchCalendarIcon} alt="" className="h-8 w-auto" />
                                </span>
                            </Button>
                        </div>

                        {/* Trust/Support Text */}
                        <p className="text-lg lg:text-xl font-bold text-gray-600 mt-6 tracking-wider">
                            {t('cta.supportedCalendars')}
                        </p>
                    </div>
                </div>
            </main>
            {/* フッター */}
            <Footer />
        </>
    );
}

export default Top;
